import Navbar from "../Navbar"
import Footer from "../Footer"
import { motion, useScroll, useTransform } from "framer-motion";
import conditionSurvey from "../../../static/images/services/condition_survery_onhire.png";

const OnOffHireSurvey = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };
  const features = [
    {
      title: "Complete Container Inspection",
      description: "Thorough external and internal inspection of all container components and structure.",
    },
    {
      title: "Structural Integrity Assessment",
      description: "Expert evaluation of container frame, floor, roof, and wall integrity.",
    },
    {
      title: "Door and Seal Verification",
      description: "Detailed inspection of all doors, hinges, seals, and locking mechanisms.",
    },
    {
      title: "Interior Condition Check",
      description: "Comprehensive documentation of interior condition, cleanliness, and functionality.",
    },
    {
      title: "Detailed Photographic Records",
      description: "Professional photographs from multiple angles documenting all aspects of the container.",
    },
    {
      title: "Liability Protection",
      description: "Professional documentation protecting both lessor and lessee interests during transitions.",
    },
  ]

  const benefits = [
    "Clear condition documentation",
    "Dispute prevention",
    "Cost tracking",
    "Lease compliance",
    "Professional records",
    "Quick processing",
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] mt-20 overflow-hidden">
        <img
          src={conditionSurvey}
          alt="Condition Survey Import Export"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">On/Off Hire Survey</h1>
              <p className="text-xl text-blue-100">
                Professional on-hire and off-hire surveys of marine containers with detailed documentation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-semibold text-orange mb-6">Service Overview</h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              On-hire and off-hire surveys are critical for protecting the interests of both container owners and
              lessees during equipment transitions. Our experienced surveyors provide detailed condition assessments
              that serve as official records of container status at the point of transfer.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We document every aspect of the container, from structural integrity to operational functionality,
              ensuring clear baseline documentation for lease terms and wear conditions. Our professional reports help
              prevent disputes and provide transparency throughout the lease cycle.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Key Features */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-6 text-center">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Why Choose Our Service</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-orange mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-lg text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Our Process</h2>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mx-auto">
              {[
                {
                  num: "1",
                  title: "Arrange Inspection",
                  desc: "Schedule survey at container location for on-hire or off-hire documentation",
                },
                {
                  num: "2",
                  title: "Full Inspection",
                  desc: "Complete visual and functional inspection of all container components",
                },
                {
                  num: "3",
                  title: "Documentation",
                  desc: "Comprehensive photography documenting condition of all elements",
                },
                {
                  num: "4",
                  title: "Detailed Report",
                  desc: "Professional assessment with condition ratings and observations",
                },
                {
                  num: "5",
                  title: "Official Record",
                  desc: "Report serves as official baseline documentation for lease records",
                },
              ].map((step, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange text-white font-semibold">
                      {step.num}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  )
}

export default OnOffHireSurvey;